import * as React from 'react';

import { useTranslations } from '@rematter/modules/transportation/i18n/useTranslations';
import { Button, LocalizedDate, LegacyMenu } from '@rematter/ui-web';
import {
  DataGrid,
  DataGridColumn,
  HeaderIcons,
  Pagination,
  type PaginationProps,
} from '@rematter/ui-web';
import { Content, Footer, Toolbar } from '@rematter/ui-web';
import { SearchField } from '@rematter/ui-web';
import { Loading } from '@rematter/ui-web';
import { useAtom } from 'jotai';
import { PlusCircle } from 'lucide-react';

import { useAssetTypesQuery } from '../../../hooks/queries/useAssetTypesQuery';
import { TransportationRootLayout } from '../../../layouts/TransportationRootLayout';
import { AssetTypeRecord } from '../../../types';

import { AssetTypeDialog } from './AssetTypeDialog';

import {
  $limit,
  $offset,
  $searchTerm,
} from '~/modules/transportation/_state/assetTypesList.state';
import useDebouncedValue from '~/shared/hooks/useDebounce';

export const AssetTypesListView: React.FC = () => {
  const t = useTranslations();

  const [_selectedRows, setSelectedRows] = React.useState<AssetTypeRecord[]>(
    [],
  );
  const [searchTerm, setSearchTerm] = useAtom($searchTerm);
  const [offset, setOffset] = useAtom($offset);
  const [limit, setLimit] = useAtom($limit);
  const [isCreateFormOpen, setIsCreateFormOpen] = React.useState(false);

  const debouncedSearchTerm = useDebouncedValue(searchTerm ?? '', 500);

  const { data, error, isLoading, refetch } = useAssetTypesQuery({
    searchTerm: debouncedSearchTerm || undefined,
    limit,
    offset,
    sorting: ['NameAsc'],
  });

  const handlePaginationChange: PaginationProps['onChange'] = ({
    limit: newLimit,
    offset: newOffset,
  }) => {
    setLimit(newLimit);
    setOffset(newOffset);
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value || undefined);
    setOffset(0);
  };

  const ASSET_TYPE_COLUMNS: DataGridColumn<AssetTypeRecord>[] = [
    {
      key: 'name',
      header: t.assetTypesView.table.columns.name,
      icon: HeaderIcons.Text,
      resizable: true,
      sortable: true,
      cell: ({ row: record }) => record.name,
    },
    {
      key: 'description',
      header: t.assetTypesView.table.columns.description,
      icon: HeaderIcons.Text,
      resizable: true,
      sortable: true,
      grow: 1,
      cell: ({ row: record }) => record.description ?? '-',
    },
    {
      key: 'capacity',
      header: t.assetTypesView.table.columns.capacity,
      icon: HeaderIcons.Text,
      resizable: true,
      sortable: true,
      cell: ({ row: record }) => record.capacity,
    },
    {
      key: 'createdAt',
      header: t.assetTypesView.table.columns.createdAt,
      icon: HeaderIcons.Date,
      sortable: true,
      cell: ({ row: record }) => (
        <LocalizedDate date={record.timestamps.createdAt} />
      ),
    },
    {
      key: 'updatedAt',
      header: t.assetTypesView.table.columns.updatedAt,
      icon: HeaderIcons.Date,
      sortable: true,
      cell: ({ row: record }) => (
        <LocalizedDate date={record.timestamps.updatedAt} />
      ),
    },
    {
      key: 'actions',
      header: '',
      width: 150,
      pinned: 'right',
      cell: () => (
        <LegacyMenu className="border border-neutral/40 shadow-sm shadow-neutral/10">
          Test
        </LegacyMenu>
      ),
    },
  ];

  if (error) {
    return (
      <TransportationRootLayout>
        <div className="p-4 text-failure">
          {t.assetTypesView.content.error(error.message)}
        </div>
      </TransportationRootLayout>
    );
  }

  const assetTypes: AssetTypeRecord[] = data?.assetTypes ?? [];
  const total = data?.total ?? 0;

  return (
    <TransportationRootLayout className="@AssetTypesListView">
      <Toolbar>
        <div className="flex items-center gap-4">
          <SearchField
            aria-label={t.assetTypesView.toolbar.search.label}
            className="min-h-0 w-80"
            placeholder={t.assetTypesView.toolbar.search.placeholder}
            value={searchTerm ?? ''}
            onChange={handleSearch}
          />
          <Button
            onPress={() => setIsCreateFormOpen(true)}
            icon={<PlusCircle />}
          >
            {t.assetTypesView.toolbar.addType}
          </Button>
        </div>
      </Toolbar>

      <Content>
        <Loading isLoading={isLoading}>
          <DataGrid<AssetTypeRecord>
            columns={ASSET_TYPE_COLUMNS}
            dataSource={assetTypes}
            rowKey="id"
            aria-label={t.assetTypesView.content.availableTypes}
            selectionMode="multiple"
            onSelectionChange={(keys) => {
              if (keys === 'all') {
                setSelectedRows(assetTypes);
              } else {
                const selectedAssetTypes = assetTypes.filter((assetType) =>
                  keys.has(String(assetType.id)),
                );
                setSelectedRows(selectedAssetTypes);
              }
            }}
            fullWidth
          />
        </Loading>
      </Content>

      <Footer>
        <Pagination
          limit={limit}
          offset={offset}
          total={total}
          onChange={handlePaginationChange}
        />
      </Footer>

      <AssetTypeDialog
        isOpen={isCreateFormOpen}
        onClose={() => setIsCreateFormOpen(false)}
        onSuccess={() => {
          setIsCreateFormOpen(false);
          refetch();
        }}
      />
    </TransportationRootLayout>
  );
};
